import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const IDLE_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour

axios.interceptors.request.use(cfg => {
  const token = sessionStorage.getItem('wf_token');
  if (token) cfg.headers['Authorization'] = `Bearer ${token}`;
  return cfg;
});

axios.interceptors.response.use(r => r, err => {
  // Only auto-logout on 401 if we have a token (i.e., token expired/invalid)
  // Don't redirect on login page auth failures
  if (err.response?.status === 401 && sessionStorage.getItem('wf_token')) {
    const url = err.config?.url || '';
    // Don't auto-sign-out for login-related endpoints
    if (!url.includes('/api/login') && !url.includes('/api/mfa')) {
      sessionStorage.removeItem('wf_token');
      sessionStorage.removeItem('wf_user');
      window.location.href = '/';
    }
  }
  return Promise.reject(err);
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const u = sessionStorage.getItem('wf_user');
    return u ? JSON.parse(u) : null;
  });

  // MFA state for login flow
  const [mfaPending, setMfaPending] = useState(null); // { username, password }
  const idleTimerRef = useRef(null);

  const doLogout = useCallback(() => {
    sessionStorage.removeItem('wf_token');
    sessionStorage.removeItem('wf_user');
    setUser(null);
    setMfaPending(null);
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (sessionStorage.getItem('wf_token')) {
      idleTimerRef.current = setTimeout(() => {
        doLogout();
        alert('You have been signed out due to 1 hour of inactivity.');
      }, IDLE_TIMEOUT_MS);
    }
  }, [doLogout]);

  // Set up idle detection
  useEffect(() => {
    if (!user) return;
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [user, resetIdleTimer]);

  // Prevent back/forward button from breaking session
  useEffect(() => {
    if (user) {
      // Replace history state so back button doesn't go to login
      window.history.replaceState({ loggedIn: true }, '', '/');
    }

    const handlePopState = (e) => {
      // If user is logged in and browser tries to navigate back, push them forward
      if (sessionStorage.getItem('wf_token')) {
        window.history.pushState({ loggedIn: true }, '', '/');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [user]);

  const login = async (username, password, totp_code) => {
    const { data } = await axios.post('/api/login', { username, password, totp_code });
    
    // If MFA is required and no code was provided
    if (data.mfa_required) {
      setMfaPending({ username, password });
      return { mfa_required: true };
    }
    
    // Successful login
    sessionStorage.setItem('wf_token', data.token);
    const userData = { username: data.username, role: data.role };
    sessionStorage.setItem('wf_user', JSON.stringify(userData));
    setUser(userData);
    setMfaPending(null);
    // Push a new history entry so back button doesn't leave the app
    window.history.pushState({ loggedIn: true }, '', '/');
    return { ok: true };
  };

  const verifyMfa = async (totp_code) => {
    if (!mfaPending) throw new Error('No MFA session pending');
    return login(mfaPending.username, mfaPending.password, totp_code);
  };

  const cancelMfa = () => {
    setMfaPending(null);
  };

  const logout = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    doLogout();
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, mfaPending, verifyMfa, cancelMfa }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
